import useSetExport from '@components/export/useSetExport';
import { useSelector } from 'react-redux';
import { isMetricFilterAppliedSelector } from '@store/trends/base/selectors';
import { getMetricFilterMetric, getMetricFilterPctMetric } from '../dimension-table/utils';
import {
  chartIntervalSelector,
  tableCompareIntervalSelector,
  noBrushSelector,
} from '@store/trends/birdChart/selectors';
import { buildIntervalText } from '@components/export/parsers';
import { MF_LABEL } from '@components/metrics-panel-hz/utils/metricFilterUtils';
import { getCompareIntervalLabel } from '@src/components/dimension-panel-hz/utils';
import { useAtomValue } from 'jotai';
import { dodAtom } from '@components/metrics-panel-hz/atoms';
import { isTableCompareEnabledAtom } from '@src/components/dimension-panel-hz/atoms/index';
import { getErrorDimensionInfo } from '@src/utils/dimension';

function useSetErrorDimensionExport({ dimension, errorCodeInfo, data = [], totals = {} }) {
  const isMetricFilterApplied = useSelector(isMetricFilterAppliedSelector);
  const interval = useSelector(chartIntervalSelector);
  const compareInterval = useSelector(tableCompareIntervalSelector);
  const isSliceAvailable = !useSelector(noBrushSelector);
  const dodEnabled = useAtomValue(dodAtom);
  const isTableCompareEnabled = useAtomValue(isTableCompareEnabledAtom);

  const headers: any = [
    {
      key: 'name',
      title: '',
      type: 'dimension',
    },
  ];

  const { primary: metric, columnName, pctMetricKey: pctMetric } = errorCodeInfo;

  // base metric
  headers.push({ key: metric, title: columnName });

  const compareLabel = getCompareIntervalLabel({
    isSliceAvailable,
    dodEnabled,
    compareInterval,
  });

  // metric filter metric
  if (isMetricFilterApplied) {
    headers.push({ key: getMetricFilterMetric(metric), title: `[${MF_LABEL}] ` + columnName });
  }

  if (isTableCompareEnabled) {
    headers.push({ key: getMetricFilterMetric(metric), title: compareLabel });
  }

  // percentage metric
  if (metric !== pctMetric) {
    headers.push({ key: pctMetric, title: columnName + ' (%)' });

    // percentage metric filter metric
    if (isMetricFilterApplied) {
      headers.push({
        key: getMetricFilterMetric(pctMetric),
        title: `[${MF_LABEL}] ` + columnName + ' (%)',
      });
    }

    if (isTableCompareEnabled) {
      headers.push({
        key: getMetricFilterMetric(pctMetric),
        title: compareLabel,
      });
    }
  }

  // MF relative % metric
  if (isMetricFilterApplied) {
    headers.push({
      key: getMetricFilterPctMetric(metric),
      title: `[${MF_LABEL}%] ` + columnName + ' (%)',
    });
  }

  const displayName = getErrorDimensionInfo(dimension).header ?? dimension;

  useSetExport({
    pageKey: 'trends',
    sectionKey: 'dimensions-panel',
    widgetKey: `dimension-table::${displayName}`,
    headers,
    data: [totals].concat(data),
    widgetInfo: {
      title: `Dimension: ${displayName} (${buildIntervalText(interval)})`,
    },
  });
}

export default useSetErrorDimensionExport;
